import React from "react";
import {
    DayPicker,
    rangeIncludesDate,
    type DateRange,
    type Matcher,
    type Modifiers,
} from "@daypicker/react";
import classNames from "classnames";
import { useConstant } from "@hooks/useConstant";
import { getDisabledMatchers, getWeekRange } from "./DatePicker.lib";
import { DatePickerCalendarNavigation } from "./DatePickerCalendarNavigation";
import styles from "./DatePicker.module.scss";

export interface DatePickerCalendarOptions {
    className?: string;
    required?: boolean;
    showOutsideDays?: boolean;
    showWeekNumbers?: boolean;
    fixedWeeks?: boolean;
    numberOfMonths?: 1 | 2;
    isPopoverSizeFixed?: boolean;
    minDate?: Date;
    maxDate?: Date;
    disabledDates?: Matcher | Matcher[];
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
}

interface SharedCalendarProps extends DatePickerCalendarOptions {
    defaultMonth?: Date;
}

interface SingleCalendarProps extends SharedCalendarProps {
    value?: Date;
    onValueChange: (value: Date | undefined) => void;
}

interface RangeCalendarProps extends SharedCalendarProps {
    value?: DateRange;
    onValueChange: (value: DateRange | undefined) => void;
    minDays?: number;
    maxDays?: number;
    excludeDisabled?: boolean;
}

interface WeekCalendarProps extends SharedCalendarProps {
    value?: DateRange;
    onValueChange: (value: DateRange | undefined) => void;
}

const calendarClassNames = {
    months: styles.months,
    month: styles.month,
    month_caption: styles.hiddenMonthCaption,
    month_grid: styles.monthGrid,
    weekdays: styles.weekdays,
    weekday: styles.weekday,
    week: styles.week,
    week_number: styles.weekNumber,
    day: styles.day,
    day_button: styles.dayButton,
    outside: styles.outside,
    today: styles.today,
    selected: styles.selected,
    range_start: styles.rangeStart,
    range_middle: styles.rangeMiddle,
    range_end: styles.rangeEnd,
    disabled: styles.disabledDay,
};

const getCalendarProps = ({
    className,
    showOutsideDays = true,
    showWeekNumbers = false,
    fixedWeeks = true,
    numberOfMonths = 1,
    minDate,
    maxDate,
    disabledDates,
    weekStartsOn = 0,
}: SharedCalendarProps) => ({
    className: classNames(styles.calendar, className),
    classNames: calendarClassNames,
    showOutsideDays,
    showWeekNumber: showWeekNumbers,
    fixedWeeks,
    numberOfMonths,
    hideNavigation: true,
    startMonth: minDate,
    endMonth: maxDate,
    disabled: getDisabledMatchers({ minDate, maxDate, disabledDates }),
    weekStartsOn,
});

const DatePickerCalendarFrame = ({
    options,
    children,
}: {
    options: SharedCalendarProps;
    children: (
        month: Date,
        onMonthChange: (month: Date) => void,
    ) => React.ReactNode;
}) => {
    const today = useConstant(() => new Date());

    return (
        <DatePickerCalendarNavigation
            defaultMonth={options.defaultMonth ?? today}
            minDate={options.minDate}
            maxDate={options.maxDate}
            numberOfMonths={options.numberOfMonths ?? 1}
            isPopoverSizeFixed={options.isPopoverSizeFixed ?? true}
        >
            {children}
        </DatePickerCalendarNavigation>
    );
};

export const SingleDatePickerCalendar = ({
    value,
    onValueChange,
    required = false,
    ...options
}: SingleCalendarProps) => {
    const frameOptions = {
        ...options,
        required,
        defaultMonth: value ?? options.defaultMonth,
    };
    const calendarProps = getCalendarProps(frameOptions);

    return (
        <DatePickerCalendarFrame options={frameOptions}>
            {(month, onMonthChange) => (
                <DayPicker
                    {...calendarProps}
                    month={month}
                    onMonthChange={onMonthChange}
                    mode="single"
                    required={required}
                    selected={value}
                    onSelect={onValueChange}
                />
            )}
        </DatePickerCalendarFrame>
    );
};

export const RangeDatePickerCalendar = ({
    value,
    onValueChange,
    required = false,
    minDays,
    maxDays,
    excludeDisabled = true,
    ...options
}: RangeCalendarProps) => {
    const frameOptions = {
        ...options,
        required,
        defaultMonth: value?.from ?? options.defaultMonth,
    };
    const calendarProps = getCalendarProps(frameOptions);

    return (
        <DatePickerCalendarFrame options={frameOptions}>
            {(month, onMonthChange) => (
                <DayPicker
                    {...calendarProps}
                    month={month}
                    onMonthChange={onMonthChange}
                    mode="range"
                    required={required}
                    selected={value}
                    onSelect={onValueChange}
                    min={minDays}
                    max={maxDays}
                    excludeDisabled={excludeDisabled}
                    resetOnSelect
                />
            )}
        </DatePickerCalendarFrame>
    );
};

export const WeekDatePickerCalendar = ({
    value,
    onValueChange,
    required = false,
    weekStartsOn = 0,
    ...options
}: WeekCalendarProps) => {
    const selectWeek = (day: Date, modifiers: Modifiers) => {
        if (modifiers.disabled || modifiers.hidden) {
            return;
        }

        const range = getWeekRange(day, weekStartsOn);
        onValueChange(modifiers.selected && !required ? undefined : range);
    };

    const frameOptions = {
        ...options,
        required,
        weekStartsOn,
        showWeekNumbers: options.showWeekNumbers ?? true,
        defaultMonth: value?.from ?? options.defaultMonth,
    };
    const calendarProps = getCalendarProps(frameOptions);

    return (
        <DatePickerCalendarFrame options={frameOptions}>
            {(month, onMonthChange) => (
                <DayPicker
                    {...calendarProps}
                    month={month}
                    onMonthChange={onMonthChange}
                    modifiers={{
                        selected: value,
                        range_start: value?.from,
                        range_end: value?.to,
                        range_middle: (day) =>
                            value ? rangeIncludesDate(value, day, true) : false,
                    }}
                    onDayClick={selectWeek}
                    onDayKeyDown={(day, modifiers, event) => {
                        if (event.key === " " || event.key === "Enter") {
                            event.preventDefault();
                            selectWeek(day, modifiers);
                        }
                    }}
                />
            )}
        </DatePickerCalendarFrame>
    );
};
